import type { Key } from "react";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { TextArea } from "@/components/base/textarea/textarea";
import {
	QUESTION_DESCRIPTION_MAX_LENGTH,
	QuestionType,
} from "@/types/question";
import { questionTypes } from "@/utils/questionIcons";
import type { QuestionTypeOrChecklist } from "../../hooks/useQuestionForm";

type QuestionFormProps = {
	title: string;
	setTitle: (value: string) => void;
	description: string;
	setDescription: (value: string) => void;
	selectedType: QuestionTypeOrChecklist | null;
	onTypeChange: (key: Key | null) => void;
	fieldErrors: Record<string, string[]>;
	setError: (error: string | null) => void;
	setFieldErrors: React.Dispatch<
		React.SetStateAction<Record<string, string[]>>
	>;
};

export const QuestionForm = ({
	title,
	setTitle,
	description,
	setDescription,
	selectedType,
	onTypeChange,
	fieldErrors,
	setError,
	setFieldErrors,
}: QuestionFormProps) => (
	<>
		<div>
			<label className="mb-2 block font-medium text-gray-700 text-sm">
				Título <span className="text-blue-600">*</span>
			</label>
			<Input
				isInvalid={!!(fieldErrors.prompt || fieldErrors.title)}
				onChange={(value) => {
					if (value.length <= 150) {
						setTitle(value);
						setError(null);
						if (fieldErrors.prompt || fieldErrors.title) {
							setFieldErrors((prev) => {
								const newErrors = { ...prev };
								delete newErrors.prompt;
								delete newErrors.title;
								return newErrors;
							});
						}
					}
				}}
				placeholder="Nova pergunta"
				value={title}
			/>
			{(fieldErrors.prompt || fieldErrors.title) && (
				<p className="mt-1 text-red-600 text-xs">
					{(fieldErrors.prompt || fieldErrors.title)?.join(", ")}
				</p>
			)}
			<p
				className={`mt-1 text-xs ${
					title.length > 140 ? "text-orange-600" : "text-gray-500"
				}`}
			>
				{title.length}/150 caracteres
			</p>
		</div>

		{selectedType !== QuestionType.Boolean ? (
			<div>
				<label className="mb-2 block font-medium text-gray-700 text-sm">
					Descrição
				</label>
				<TextArea
					aria-label="Descrição"
					onChange={(value: string) => {
						setDescription(value.slice(0, QUESTION_DESCRIPTION_MAX_LENGTH));
					}}
					placeholder="Adicione uma descrição ou instrução para essa pergunta"
					rows={3}
					value={description}
				/>
				<p
					className={`mt-1 text-xs ${
						description.length > QUESTION_DESCRIPTION_MAX_LENGTH - 20
							? "text-orange-600"
							: "text-gray-500"
					}`}
				>
					{description.length}/{QUESTION_DESCRIPTION_MAX_LENGTH} caracteres
				</p>
			</div>
		) : null}

		<div>
			<label className="mb-2 block font-medium text-gray-700 text-sm">
				Tipo de pergunta <span className="text-blue-600">*</span>
			</label>
			<Select
				aria-label="Tipo de pergunta"
				onSelectionChange={onTypeChange}
				placeholder="Selecione o tipo de pergunta"
				selectedKey={selectedType?.toString() ?? ""}
			>
				{questionTypes.map((type) => (
					<Select.Item
						icon={type.icon as any}
						id={type.id.toString()}
						key={type.id}
					>
						{type.label}
					</Select.Item>
				))}
			</Select>
		</div>
	</>
);
